import { Text } from '../../../../../components/atoms/text/text';
import { ThresholdBar } from '../../../../../components/molecules/threshold-bar/threshold-bar';
import { useRenderContext } from '../../../context/render-context';
import * as createBlockUtils from '../../../utils/create-block';
import { ResolveFields } from '../../../utils/create-block';
import * as expressionUtils from '../../../utils/surveys/expressions';
import * as fieldUtils from '../../../utils/surveys/fields';

type SubscalesProps = {
  readonly subscales?: {
    expressionName?: string;
  }[];
};

const Subscale = ({
  expression,
  value,
}: {
  readonly expression: expressionUtils.IQuestionExpressionModelForSubscale;
  readonly value: number;
}) => (
  <div className="flex flex-col gap-1">
    <Text size="sm" weight="semibold">
      {expression.measureName}
    </Text>
    <div className="flex row items-center gap-2">
      <ThresholdBar maxValue={expression.maxValue} thresholds={[]} value={value} />
      <span>
        <Text size="xs" weight="semibold">{`${value}/${expression.maxValue}`}</Text>
      </span>
    </div>
  </div>
);

const Subscales = ({ subscales }: SubscalesProps) => {
  const { survey, answers } = useRenderContext();
  if (!survey) return <div>No survey</div>;
  if (!answers) return <div>No answers</div>;
  if (!subscales?.length) return <div>No subscale added.</div>;

  return (
    <div className="flex flex-col gap-2">
      {subscales.map((subscale, index) => {
        const expression = expressionUtils.getExpressionFromSurvey(survey, subscale.expressionName);

        if (!expression || !subscale.expressionName)
          return <div key={`${survey.id}-expression-${index}`}>No expression selected.</div>;

        const isSubscale = expressionUtils.isSubscaleExpression(expression);
        if (isSubscale) {
          return (
            <Subscale
              expression={expression}
              key={`${subscale.expressionName}-${index}`}
              value={expressionUtils.getMeasureValue(answers[subscale.expressionName])}
            />
          );
        } else {
          return <div key={`${survey.id}-expression-${index}`}>This expression is not a subscale.</div>;
        }
      })}
    </div>
  );
};

const resolveFields: ResolveFields<SubscalesProps> = ({ props: { survey } }, { fields }) => ({
  ...fields,
  ...(survey && {
    subscales: {
      type: 'array',
      label: 'Subscales',
      arrayFields: {
        expressionName: fieldUtils.createExpressionField(survey, expressionUtils.ExpressionTypes.SUBSCALE),
      },
    },
  }),
});

const createSubscaleBlock = createBlockUtils.makeBlockFactory<SubscalesProps>(
  { withExampleAnswersJson: true },
  {
    label: 'Subscales',
    render: Subscales,
    resolveFields,
  }
);

export { createSubscaleBlock, resolveFields, Subscales, type SubscalesProps };
